"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  FileText,
  UserCheck,
  Sparkles,
  CreditCard,
  Copyright,
  Ban,
  AlertTriangle,
  Scale,
  RefreshCw,
  Mail,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";

const sections = [
  { id: "introduction", title: "1. Giới thiệu", icon: FileText },
  { id: "account", title: "2. Tài khoản người dùng", icon: UserCheck },
  { id: "service", title: "3. Dịch vụ JB AI", icon: Sparkles },
  { id: "payment", title: "4. Thanh toán & gói dịch vụ", icon: CreditCard },
  { id: "ip", title: "5. Sở hữu trí tuệ", icon: Copyright },
  { id: "prohibited", title: "6. Hành vi bị cấm", icon: Ban },
  { id: "disclaimer", title: "7. Tuyên bố miễn trừ", icon: AlertTriangle },
  { id: "law", title: "8. Luật áp dụng", icon: Scale },
  { id: "changes", title: "9. Thay đổi điều khoản", icon: RefreshCw },
  { id: "contact", title: "10. Liên hệ", icon: Mail },
];

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState<string>("introduction");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero section */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-100/60 via-background to-teal-50 dark:from-orange-600/10 dark:via-background dark:to-teal-900/20" />
        <div className="relative mx-auto max-w-6xl px-6 py-12 sm:px-8 sm:py-16">
          <Link
            href="/"
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Quay lại trang chủ
          </Link>

          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10 ring-1 ring-orange-500/20">
              <FileText className="h-6 w-6 text-orange-600" />
            </div>
            <span className="inline-flex items-center rounded-full bg-orange-500/10 px-3 py-1 text-xs font-medium text-orange-700 ring-1 ring-inset ring-orange-500/20 dark:text-orange-400">
              Quy định sử dụng
            </span>
          </div>

          <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Điều khoản dịch vụ
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Vui lòng đọc kỹ các điều khoản dưới đây trước khi sử dụng JB AI. Bằng
            việc sử dụng dịch vụ, bạn đồng ý tuân thủ toàn bộ quy định này.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            Cập nhật lần cuối:{" "}
            <span className="font-medium text-foreground">22/05/2026</span>
          </p>
        </div>
      </div>

      {/* Main content */}
      <div className="mx-auto max-w-6xl px-6 py-10 sm:px-8 lg:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[260px_1fr]">
          {/* Table of Contents */}
          <aside className="lg:sticky lg:top-8 lg:h-fit">
            <div className="rounded-xl border border-border bg-card p-4 shadow-card">
              <h2 className="mb-3 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Nội dung
              </h2>
              <nav className="space-y-0.5">
                {sections.map(({ id, title, icon: Icon }) => {
                  const isActive = activeSection === id;
                  return (
                    <button
                      key={id}
                      onClick={() => scrollToSection(id)}
                      className={`group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-all ${
                        isActive
                          ? "bg-orange-500/10 font-medium text-orange-700 dark:text-orange-400"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 shrink-0 ${
                          isActive ? "text-orange-600" : ""
                        }`}
                      />
                      <span className="truncate">{title}</span>
                      {isActive && (
                        <ChevronRight className="ml-auto h-3.5 w-3.5 text-orange-600" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Article content */}
          <article className="min-w-0">
            <div className="space-y-12">
              <Section id="introduction" title="1. Giới thiệu">
                <p>
                  Chào mừng bạn đến với <strong>JB AI</strong> — nền tảng học thi
                  thông minh được vận hành bởi Job & Banking thông qua website{" "}
                  <a
                    href="https://web.jb.com.vn"
                    className="text-teal-600 hover:underline dark:text-teal-400"
                  >
                    web.jb.com.vn
                  </a>
                  . Các Điều khoản dịch vụ này (sau đây gọi là "Điều khoản") quy
                  định mối quan hệ pháp lý giữa bạn và chúng tôi khi bạn sử dụng
                  dịch vụ.
                </p>
                <p>
                  Bằng việc đăng ký tài khoản, truy cập hoặc sử dụng JB AI, bạn xác
                  nhận đã đọc, hiểu và đồng ý bị ràng buộc bởi các Điều khoản này
                  cũng như{" "}
                  <Link
                    href="/privacy"
                    className="text-teal-600 hover:underline dark:text-teal-400"
                  >
                    Chính sách bảo mật
                  </Link>{" "}
                  của chúng tôi.
                </p>
              </Section>

              <Section id="account" title="2. Tài khoản người dùng">
                <h3>2.1. Đăng ký tài khoản</h3>
                <ul>
                  <li>Bạn phải từ <strong>13 tuổi trở lên</strong> để đăng ký tài khoản JB AI</li>
                  <li>Cung cấp thông tin chính xác, đầy đủ và cập nhật khi có thay đổi</li>
                  <li>Mỗi cá nhân chỉ được sử dụng một tài khoản chính</li>
                </ul>
                <h3>2.2. Bảo mật tài khoản</h3>
                <ul>
                  <li>Bạn chịu trách nhiệm bảo mật thông tin đăng nhập</li>
                  <li>Mọi hoạt động xảy ra trên tài khoản đều được xem là do bạn thực hiện</li>
                  <li>Thông báo ngay cho chúng tôi khi phát hiện truy cập trái phép</li>
                </ul>
                <h3>2.3. Chấm dứt tài khoản</h3>
                <p>
                  Chúng tôi có quyền tạm ngừng hoặc xóa tài khoản vi phạm Điều khoản
                  này mà không cần thông báo trước. Bạn cũng có thể yêu cầu xóa tài
                  khoản bất kỳ lúc nào.
                </p>
              </Section>

              <Section id="service" title="3. Dịch vụ JB AI">
                <p>JB AI cung cấp các tính năng sau:</p>
                <ul>
                  <li>Lộ trình học cá nhân hóa cho các kỳ thi ngân hàng</li>
                  <li>Ngân hàng đề thi và câu hỏi luyện tập</li>
                  <li>Trợ lý AI hỗ trợ giải thích và ôn luyện</li>
                  <li>Review và đánh giá từ cộng đồng người học</li>
                  <li>Bảng xếp hạng, hệ thống điểm thưởng (kim cương)</li>
                </ul>
                <p>
                  Chúng tôi nỗ lực duy trì dịch vụ ổn định nhưng <strong>không cam
                  kết</strong> dịch vụ luôn sẵn sàng 100%. Có thể xảy ra gián đoạn
                  do bảo trì, nâng cấp hoặc nguyên nhân khách quan.
                </p>
                <p>
                  Nếu nội dung trên Lộ trình, đề thi, câu hỏi ghi chú do AI tạo ra, bạn cần kiểm tra lại độc lập. Người dùng cần
                  tự đánh giá và không nên xem là tư vấn chuyên môn tuyệt đối.
                  Nếu nội dung Lộ trình, đề thi, câu hỏi có gắn tên tác giả thì tác giả đó chịu trách nhiệm về tính xác thực của nội dung.
                </p>
              </Section>

              <Section id="payment" title="4. Thanh toán & gói dịch vụ">
                <p>JB AI cung cấp cả gói miễn phí và gói trả phí:</p>
                <ul>
                  <li>
                    <strong>Gói miễn phí</strong>: truy cập các tính năng cơ bản,
                    có giới hạn về số lượng câu hỏi và lượt sử dụng AI
                  </li>
                  <li>
                    <strong>Kim cương / gói trả phí</strong>: mở khóa toàn bộ nội
                    dung, tăng giới hạn sử dụng AI, các tính năng nâng cao
                  </li>
                </ul>
                <h3>4.1. Hoàn tiền</h3>
                <p>
                  Các giao dịch mua kim cương hoặc gói dịch vụ trên JB AI là{" "}
                  <strong>không hoàn lại</strong>, trừ trường hợp lỗi từ hệ thống
                  hoặc theo quy định bắt buộc của pháp luật.
                </p>
                <h3>4.2. Thay đổi giá</h3>
                <p>
                  Chúng tôi có quyền điều chỉnh giá dịch vụ và sẽ thông báo trước
                  ít nhất 14 ngày qua email hoặc thông báo trong ứng dụng.
                </p>
              </Section>

              <Section id="ip" title="5. Sở hữu trí tuệ">
                <h3>5.1. Quyền của JB AI</h3>
                <p>
                  Toàn bộ nội dung trên JB AI (bao gồm nhưng không giới hạn: đề
                  thi, lộ trình, văn bản, hình ảnh, logo, mã nguồn, thiết kế) là
                  tài sản của Job & Banking hoặc đối tác cấp phép, được bảo hộ
                  theo luật sở hữu trí tuệ Việt Nam và quốc tế.
                </p>
                <h3>5.2. Quyền sử dụng của người dùng</h3>
                <p>
                  Chúng tôi cấp cho bạn quyền sử dụng cá nhân, không độc quyền,
                  không được chuyển nhượng để truy cập nội dung cho mục đích học
                  tập. Bạn{" "}
                  <strong>
                    không được sao chép, phân phối, bán lại hoặc khai thác thương
                    mại
                  </strong>{" "}
                  nội dung của JB AI dưới mọi hình thức.
                </p>
                <h3>5.3. Nội dung do người dùng tạo</h3>
                <p>
                  Khi bạn đăng review, bình luận hoặc nội dung khác trên JB AI,
                  bạn cấp cho chúng tôi quyền sử dụng miễn phí, toàn cầu để hiển
                  thị, sửa đổi và phân phối nội dung đó trong phạm vi dịch vụ.
                </p>
              </Section>

              <Section id="prohibited" title="6. Hành vi bị cấm">
                <p>Khi sử dụng JB AI, bạn không được:</p>
                <ul>
                  <li>Vi phạm pháp luật Việt Nam hoặc quy định quốc tế hiện hành</li>
                  <li>Mạo danh người khác hoặc cung cấp thông tin sai sự thật</li>
                  <li>Sử dụng bot, script tự động để truy cập hoặc khai thác dữ liệu</li>
                  <li>Tấn công, can thiệp vào hệ thống, cơ sở dữ liệu của JB AI</li>
                  <li>Phát tán nội dung độc hại, spam, lừa đảo</li>
                  <li>Sao chép, bán lại nội dung dịch vụ</li>
                  <li>Sử dụng dịch vụ để thực hiện gian lận thi cử</li>
                  <li>Đăng tải nội dung vi phạm thuần phong mỹ tục, kích động bạo lực, thù hận</li>
                </ul>
                <p>
                  Vi phạm bất kỳ điều nào ở trên có thể dẫn đến việc tài khoản bị
                  khóa vĩnh viễn và có thể bị truy cứu trách nhiệm pháp lý.
                </p>
              </Section>

              <Section id="disclaimer" title="7. Tuyên bố miễn trừ trách nhiệm">
                <p>
                  Dịch vụ JB AI được cung cấp "nguyên trạng" (as-is) và "có thể sử
                  dụng" (as-available). Trong phạm vi pháp luật cho phép, chúng tôi{" "}
                  <strong>không bảo đảm</strong>:
                </p>
                <ul>
                  <li>Dịch vụ sẽ đáp ứng mọi yêu cầu cụ thể của bạn</li>
                  <li>Dịch vụ luôn hoạt động không gián đoạn, không lỗi</li>
                  <li>Kết quả học tập, thi cử của bạn (kết quả phụ thuộc vào nỗ lực cá nhân)</li>
                  <li>Tính chính xác tuyệt đối của nội dung do AI tạo ra</li>
                </ul>
                <p>
                  Chúng tôi không chịu trách nhiệm cho các thiệt hại gián tiếp,
                  ngẫu nhiên hoặc hệ quả phát sinh từ việc sử dụng dịch vụ, trong
                  giới hạn tối đa được pháp luật cho phép.
                </p>
              </Section>

              <Section id="law" title="8. Luật áp dụng & giải quyết tranh chấp">
                <p>
                  Các Điều khoản này được điều chỉnh bởi pháp luật{" "}
                  <strong>nước Cộng hòa Xã hội Chủ nghĩa Việt Nam</strong>. Mọi
                  tranh chấp phát sinh sẽ được ưu tiên giải quyết thông qua thương
                  lượng, hòa giải.
                </p>
                <p>
                  Trường hợp không thể hòa giải, tranh chấp sẽ được đưa ra Tòa án
                  có thẩm quyền tại Việt Nam để giải quyết theo quy định pháp luật
                  hiện hành.
                </p>
              </Section>

              <Section id="changes" title="9. Thay đổi điều khoản">
                <p>
                  Chúng tôi có quyền cập nhật Điều khoản này theo thời gian. Khi
                  có thay đổi quan trọng, chúng tôi sẽ thông báo qua email hoặc
                  thông báo nổi bật trên website ít nhất <strong>7 ngày</strong>{" "}
                  trước khi có hiệu lực.
                </p>
                <p>
                  Việc tiếp tục sử dụng dịch vụ sau ngày có hiệu lực đồng nghĩa
                  với việc bạn chấp nhận các điều khoản mới. Nếu không đồng ý, bạn
                  có thể ngừng sử dụng và yêu cầu xóa tài khoản.
                </p>
              </Section>

              <Section id="contact" title="10. Liên hệ">
                <p>Mọi thắc mắc về Điều khoản dịch vụ, vui lòng liên hệ:</p>
                <div className="not-prose mt-4 rounded-xl border border-border bg-gradient-to-br from-orange-100/60 to-teal-50 p-5 dark:from-orange-600/10 dark:to-teal-900/20">
                  <div className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />
                    <div className="space-y-1 text-sm">
                      <p className="font-semibold text-foreground">Job & Banking — JB AI</p>
                      <p className="text-muted-foreground">
                        Email:{" "}
                        <a
                          href="mailto:lienhejb@gmail.com"
                          className="font-medium text-teal-600 hover:underline dark:text-teal-400"
                        >
                          lienhejb@gmail.com
                        </a>
                      </p>
                      <p className="text-muted-foreground">
                        Website:{" "}
                        <a
                          href="https://web.jb.com.vn"
                          className="font-medium text-teal-600 hover:underline dark:text-teal-400"
                        >
                          web.jb.com.vn
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </Section>
            </div>

            {/* Footer link */}
            <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-8">
              <p className="text-sm text-muted-foreground">
                © 2026 JB AI. Tất cả quyền được bảo lưu.
              </p>
              <Link
                href="/privacy"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 hover:underline dark:text-teal-400"
              >
                Xem Chính sách bảo mật
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-8">
      <h2 className="mb-4 font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {title}
      </h2>
      <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground [&_a]:font-medium [&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground [&_li]:my-1.5 [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-6">
        {children}
      </div>
    </section>
  );
}
